/**
 * AuthContext — dashboard session state.
 *
 * Pattern (D-10): on mount, read localStorage[StorageKey.AUTH_TOKEN],
 * jwt-decode it, validate exp. If valid, hydrate user state; if expired or
 * malformed, clear the token silently.
 *
 * Adapted from black-mirror's AuthContext, which takes a `value` prop (the
 * wrapper app manages state). Sales Coach owns state here because our
 * <App> bootstrap is simpler and there's no wrapping router shell that
 * already holds auth.
 *
 * JWT payload shape matches Phase 3's src/dashboard/auth/jwt.ts
 * DashboardJwtPayload: { sub, email, slackId, internalRole, iat, exp }.
 *
 * Exposes:
 *   - user: AuthUser | null
 *   - isAuthenticated: boolean
 *   - setAuth(token): hydrate + persist
 *   - clearAuth(): drop token + state
 *
 * The 401 bridge in lib/query-client.ts should register `clearAuth` as the
 * unauthorized handler at App-root mount time (Plan 04-09).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { jwtDecode } from 'jwt-decode';

import { StorageKey } from '@/constants/storageKeys';

export interface AuthUser {
  email: string;
  slackId: string | null;
  internalRole: string;
}

interface JwtPayloadShape {
  sub: string;
  email: string;
  slackId: string | null;
  internalRole: string;
  iat: number;
  exp: number;
}

interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  setAuth: (token: string) => void;
  clearAuth: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function readStoredToken(): string | null {
  try {
    return localStorage.getItem(StorageKey.AUTH_TOKEN);
  } catch {
    return null;
  }
}

function decodeToken(token: string): AuthUser | null {
  try {
    const payload = jwtDecode<JwtPayloadShape>(token);
    if (!payload || typeof payload.exp !== 'number') return null;

    const nowSec = Math.floor(Date.now() / 1000);
    if (payload.exp <= nowSec) return null;

    if (
      typeof payload.email !== 'string' ||
      typeof payload.internalRole !== 'string'
    ) {
      return null;
    }

    return {
      email: payload.email,
      slackId: payload.slackId ?? null,
      internalRole: payload.internalRole,
    };
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  // Hydrate synchronously during the initial render (D-10 + Plan 04-09
  // RequireAuth fix): if we defer hydration to a useEffect, the first render
  // sees user=null and RequireAuth redirects authenticated users to /login
  // before the effect runs. A synchronous read avoids that flicker and the
  // premature redirect.
  const [user, setUser] = useState<AuthUser | null>(() => {
    const token = readStoredToken();
    if (!token) return null;
    const decoded = decodeToken(token);
    if (decoded) return decoded;
    try {
      localStorage.removeItem(StorageKey.AUTH_TOKEN);
    } catch {
      // noop
    }
    return null;
  });

  // Re-run on mount in case the storage changed between module load and first
  // render (rare — e.g., test harness that injects tokens after mount). We
  // keep this as a no-op safeguard for the already-hydrated path.
  //
  // Also probes `/auth/status` — when the backend reports AUTH_ENABLED=false
  // (Tailscale-only dev), we stamp a local synthetic admin user so RequireAuth
  // passes without a token. The backend deliberately does NOT return a user
  // payload (defense-in-depth so a prod leak can't reveal role shape) — the
  // SPA owns the synthetic identity client-side. Prod backends always return
  // `{authEnabled: true}` so this branch is a no-op.
  useEffect(() => {
    const token = readStoredToken();
    if (!token) {
      fetch('/salescoach/auth/status', { credentials: 'omit' })
        .then((r) => (r.ok ? r.json() : null))
        .then((status: { authEnabled: boolean } | null) => {
          if (status && status.authEnabled === false) {
            setUser({ email: 'dev@local', slackId: null, internalRole: 'full' });
          } else {
            setUser((prev) => (prev === null ? prev : null));
          }
        })
        .catch(() => {
          setUser((prev) => (prev === null ? prev : null));
        });
      return;
    }
    const decoded = decodeToken(token);
    if (decoded) {
      setUser((prev) =>
        prev && prev.email === decoded.email ? prev : decoded,
      );
    } else {
      try {
        localStorage.removeItem(StorageKey.AUTH_TOKEN);
      } catch {
        // noop
      }
      setUser(null);
    }
  }, []);

  const setAuth = useCallback((token: string) => {
    const decoded = decodeToken(token);
    if (!decoded) return;
    try {
      localStorage.setItem(StorageKey.AUTH_TOKEN, token);
    } catch {
      // noop
    }
    setUser(decoded);
  }, []);

  const clearAuth = useCallback(() => {
    try {
      localStorage.removeItem(StorageKey.AUTH_TOKEN);
    } catch {
      // noop
    }
    setUser(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, isAuthenticated: user !== null, setAuth, clearAuth }),
    [user, setAuth, clearAuth],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
